// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportPartGaugeConstants.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import localizer from 'share/localization/Izenda.Localizer';
import constants from 'logic/data/Izenda.ReportPartFieldConstants';
import { CHART_TYPE } from 'logic/data/Izenda.ChartDefaultConfig';
import { values } from 'lodash/fp';

const FUNCTION = constants.FUNCTION;
const FORMAT_KEY = constants.FORMAT_KEY;

const CHART_TYPES = [
  {
    get label() {
      return localizer.getText('REPORT_PART_GAUGE_CONSTANTS_LABEL_LINEAR_GAUGE');
    },
    value: CHART_TYPE.LinearGauge,
    className: 'gauge-linear'
  },
  {
    get label() {
      return localizer.getText('REPORT_PART_GAUGE_CONSTANTS_LABEL_SIMPLE_GAUGE');
    },
    value: CHART_TYPE.SimpleGauge,
    className: 'gauge-simple'
  },
  {
    get label() {
      return localizer.getText('REPORT_PART_GAUGE_CONSTANTS_LABEL_SOLID_GAUGE');
    },
    value: CHART_TYPE.SolidGauge,
    className: 'gauge-solid'
  }
];

/* Container which defined fields to get data */
const ContainerName = {
  LABELS: 'labels',
  VALUES: 'values',
  SEPARATORS: 'separators',
  THRESHOLD: 'threshold',
  SUP_KPI: 'sup_kpi',
  DRILLDOWN: 'labels', // labels
  METRIC_DROPDOWN: 'values' // values
};

/* Default function for each fields based on its container */
const DefaultFunction = {};

// SOLID GAUGE
DefaultFunction[CHART_TYPE.SolidGauge] = {};

// SOLID GAUGE -  labels container
DefaultFunction[CHART_TYPE.SolidGauge][ContainerName.LABELS] = {
  Datetime: {
    FUNCTION: FUNCTION.GROUP,
    FORMAT: FORMAT_KEY.YEAR
  },
  Time: {
    FUNCTION: FUNCTION.GROUP,
    FORMAT: FORMAT_KEY.DEFAULT_TIME
  },

  Others: {
    FUNCTION: FUNCTION.GROUP,
    FORMAT: FORMAT_KEY.NONE
  }
};

// SOLID GAUGE -  values container
DefaultFunction[CHART_TYPE.SolidGauge][ContainerName.VALUES] = {
  Numeric: {
    FUNCTION: FUNCTION.SUM,
    FORMAT: FORMAT_KEY.NONE
  },

  Money: {
    FUNCTION: FUNCTION.SUM,
    FORMAT: FORMAT_KEY.NONE
  },

  Others: {
    FUNCTION: FUNCTION.COUNT,
    FORMAT: FORMAT_KEY.NONE
  }
};

// SOLID GAUGE -  separators container (same as labels)
DefaultFunction[CHART_TYPE.SolidGauge][ContainerName.SEPARATORS] =
  DefaultFunction[CHART_TYPE.SolidGauge][ContainerName.LABELS];

// SOLID GAUGE -  threshold container (same as values)
DefaultFunction[CHART_TYPE.SolidGauge][ContainerName.THRESHOLD] =
  DefaultFunction[CHART_TYPE.SolidGauge][ContainerName.VALUES];

// LINEAR (same as solid gauge)
DefaultFunction[CHART_TYPE.LinearGauge] = DefaultFunction[CHART_TYPE.SolidGauge];

// SIMPLE (same as solid/linear gauge and has supplementary KPI)
DefaultFunction[CHART_TYPE.SimpleGauge] = DefaultFunction[CHART_TYPE.SolidGauge];
DefaultFunction[CHART_TYPE.SimpleGauge][ContainerName.SUP_KPI] =
  DefaultFunction[CHART_TYPE.LinearGauge][ContainerName.VALUES];

/* Maximum number of fields for each container */
const MaximumField = {};

// Solid
MaximumField[CHART_TYPE.SolidGauge] = {};
// MaximumField[CHART_TYPE.SolidGauge][ContainerName.VALUES] = 5;
MaximumField[CHART_TYPE.SolidGauge][ContainerName.SEPARATORS] = 1;
MaximumField[CHART_TYPE.SolidGauge][ContainerName.THRESHOLD] = 1;

// Linear (same as solid)
MaximumField[CHART_TYPE.LinearGauge] = MaximumField[CHART_TYPE.SolidGauge];

// Simple (same as solid/linear and has supplementary KPI)
MaximumField[CHART_TYPE.SimpleGauge] = MaximumField[CHART_TYPE.SolidGauge];
MaximumField[CHART_TYPE.SimpleGauge][ContainerName.SUP_KPI] = 1;

/* Messages */
const Message = {
  get M01() {
    return localizer.getText('REPORT_PART_GAUGE_CONSTANTS_MESSAGE_LOW_VALUE_MUST_LESS_THAN_HIGH_VALUE');
  },
  get M02() {
    return localizer.getText('GAUGE_MESSAGE_THRESHOL_NUMBER');
  },
  get M04() {
    return localizer.getText('REPORT_PART_GAUGE_CONSTANTS_MESSAGE_THRESHOLDS_MUST_BE_WITHIN_SCALE_FORM_SCALE_TO');
  },
  get M05() {
    return localizer.getText('COMMON_MESSAGES_MUST_BE_FUNCTION');
  },
  get M06() {
    return localizer.getText('GAUGE_MESSAGE_SCALE_RANGE');
  },
  get M07() {
    return localizer.getText('GAUGE_MESSAGE_THRESHOLD_MAX_VALUE');
  },
  get M08() {
    return localizer.getText('GAUGE_MESSAGE_THRESHOLD_MIN_VALUE');
  }
};

/* Setting value: for threshold & supplementary KPI */
const SETTING_VALUES = {
  STATIC: {
    get label() {
      return localizer.getText('REPORT_PART_GAUGE_CONSTANTS_LABEL_STATIC');
    },
    value: 'static'
  },
  DYNAMIC: {
    get label() {
      return localizer.getText('REPORT_PART_GAUGE_CONSTANTS_LABEL_DYNAMIC');
    },
    value: 'dynamic'
  }
};

const THRESHOLD_SETTINGS = SETTING_VALUES;

const THRESHOLD_OPERATORS = {
  LESS_THAN: {
    get label() {
      return localizer.getText('REPORT_PART_GAUGE_CONSTANTS_LABEL_LESS_THAN');
    },
    value: 'less than'
  },
  BETWEEN: {
    get label() {
      return localizer.getText('REPORT_PART_GAUGE_CONSTANTS_LABEL_BETWEEN');
    },
    value: 'between'
  },
  GREAT_THAN: {
    get label() {
      return localizer.getText('REPORT_PART_GAUGE_CONSTANTS_LABEL_GREATER_THAN');
    },
    value: 'greater than'
  }
};

const THRESHOLD_VALUES = {
  LOW: {
    get label() {
      return localizer.getText('COMMON_LABEL_LOW');
    },
    value: 'low',
    defautColor: '#55BF3B',
    defautOperator: THRESHOLD_OPERATORS.LESS_THAN.value
  },
  TARGET: {
    get label() {
      return localizer.getText('REPORT_PART_GAUGE_CONSTANTS_LABEL_TARGET');
    },
    value: 'target',
    defautColor: '#DDDF0D',
    defautOperator: THRESHOLD_OPERATORS.LESS_THAN.value
  },
  HIGH: {
    get label() {
      return localizer.getText('COMMON_LABEL_HIGH');
    },
    value: 'high',
    defautColor: '#DF5353',
    defautOperator: THRESHOLD_OPERATORS.GREAT_THAN.value
  }
};

const SUPP_SETTINGS = SETTING_VALUES;

export default {
  ContainerName,
  MaximumField,
  Message,
  DefaultFunction,
  DefaultChartType: CHART_TYPE.SolidGauge,
  CHART_TYPES,
  SETTING_VALUES,
  THRESHOLD_SETTINGS,
  THRESHOLD_OPERATORS,
  THRESHOLD_VALUES,

  SUPP_SETTINGS,

  ITEM_PER_ROWS: 3,

  getOptions(key) {
    const data = this[key] || {};

    return values(data);
  },
  DEFAULT_SETTING_VALUE: {
    borderDefault: {
      'izEntire.borderColor': '#000',
      'izEntire.borderWidth': undefined,
      'izEntire.borderStyle': 'solid'
    },
    borderNew: {
      'izEntire.borderColor': undefined,
      'izEntire.borderWidth': undefined,
      'izEntire.borderStyle': undefined
    },
    refreshIntervalDefault: {
      enable: false,
      isAll: true,
      latestRecord: 0,
      updateInterval: 0
    }
  }
};



// WEBPACK FOOTER //
// ./ui/logic/data/Izenda.ReportPartGaugeConstants.js